import { Box, Stack } from '@mui/material';
import { brandColors, colors, fontFamilies, radius } from '../../designTokens';

export interface CairnStonesProps {
  /** Label of the base stone (assessment / diagnosis). */
  base: string;
  /** Label of the middle stone (wellbeing / balance). */
  middle: string;
  /** Label of the top stone (growth). */
  top: string;
  /**
   * Surface the cairn sits on. On the dark (petroleum) hero card the base stone switches to névoa so
   * it stays visible against the background.
   */
  tone?: 'light' | 'dark';
}

interface Stone {
  key: string;
  label: string;
  width: string;
  height: number;
  background: string;
  color: string;
}

/**
 * Hero graphic: three aligned pebbles stacked as a cairn, largest at the bottom. Pure colour blocks
 * (no gradients or shadows), as the brand manual asks. Petroleum = foundations,
 * mint = wellbeing, coral = growth.
 */
export default function CairnStones({ base, middle, top, tone = 'light' }: CairnStonesProps) {
  const onDark = tone === 'dark';
  const stones: Stone[] = [
    { key: 'top', label: top, width: '50%', height: 96, background: brandColors.coral, color: colors.sobreAcento },
    { key: 'middle', label: middle, width: '75%', height: 112, background: brandColors.menta, color: colors.tinta },
    {
      key: 'base',
      label: base,
      width: '100%',
      height: 128,
      background: onDark ? brandColors.nevoa : brandColors.petroleo,
      color: onDark ? colors.tinta : brandColors.offWhite,
    },
  ];

  return (
    <Stack
      role="img"
      aria-label={`${top} · ${middle} · ${base}`}
      data-testid="cairn-stones"
      spacing={1.5}
      alignItems="center"
      sx={{ width: '100%', maxWidth: 400, mx: 'auto' }}
    >
      {stones.map((stone) => (
        <Box
          key={stone.key}
          data-testid={`cairn-stone-${stone.key}`}
          sx={{
            width: stone.width,
            height: stone.height,
            borderRadius: radius.seixo,
            backgroundColor: stone.background,
            color: stone.color,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: fontFamilies.sans,
            fontWeight: 500,
            fontSize: '1.25rem',
          }}
          aria-hidden="true"
        >
          {stone.label}
        </Box>
      ))}
    </Stack>
  );
}
